/**
 * Step2PrepareAttestation.js
 *
 * WHAT: Step 2 of the campaign workflow -- builds and previews the attestation scope.
 * WHY:  Before assigning attesters, the campaign creator must see which database users
 *       will be included, grouped by their enterprise manager. This step generates the
 *       campaign report table, lets the creator filter by user status and privilege level,
 *       and validates that the scope is correct before proceeding to attester assignment.
 */
import React, { useState, useEffect } from 'react';
import { getPrivilegeLevelStyle, PRIVILEGE_LEVEL_ORDER } from '../../utils/privilegeLevels';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Attestation scope preview showing database users grouped by manager with filtering controls.
 * WHY:  Gives campaign creators confidence that the right users are in scope before the
 *       campaign moves to attester assignment, reducing rework and ensuring audit completeness.
 */
const AttestationPreparation = ({ campaign, activeStep, userFilter, setUserFilter, accessFilters, setAccessFilters, includeSystemAccounts, setIncludeSystemAccounts }) => {
  // MSSQL multi-DB: the `database_name` column holds the SQL Server INSTANCE
  // identifier; the physical databases a grant spans are in `physical_databases`.
  // For MSSQL we relabel the column to "Instance Name" and add a "DB Names" column.
  const isMssql = (campaign?.database_type || '').toUpperCase() === 'MSSQL';
  const [attestationData, setAttestationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({
    db_username: '',
    database_name: '',
    physical_databases: '',
    status: '',
    user_display_name: '',
    user_department: '',
    manager_name: '',
    privilege_level: '',
    risk_level: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Get unique privilege levels from data
  const getUniquePrivilegeLevels = () => {
    if (!attestationData) return [];

    let allUsers = [];
    if (attestationData.managers) {
      allUsers = attestationData.managers.flatMap(manager => manager.assigned_users);
    }
    if (attestationData.unmatched_users) {
      allUsers = [...allUsers, ...attestationData.unmatched_users];
    }

    const uniqueLevels = [...new Set(allUsers.map(user => user.privilege_level).filter(Boolean))];
    return uniqueLevels.sort();
  };

  useEffect(() => {
    if (campaign && campaign.campaign_id && activeStep === 2) {
      // Clear previous data before loading fresh data to avoid showing stale/duplicate data
      setAttestationData(null);
      loadAttestationData();
    }
  }, [campaign, activeStep]);

  // Auto-select all privilege level filters only on initial data load
  // Don't reset if user has already made filter selections
  useEffect(() => {
    if (attestationData) {
      const uniqueLevels = getUniquePrivilegeLevels();
      // Only auto-select if accessFilters is empty or doesn't have any of the current privilege levels
      const hasExistingFilters = uniqueLevels.some(level => level in accessFilters);
      if (!hasExistingFilters) {
        const allFiltersChecked = {};
        uniqueLevels.forEach(level => {
          allFiltersChecked[level] = true;
        });
        setAccessFilters(allFiltersChecked);
      }
    }
  }, [attestationData]);

  const loadAttestationData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Note: Report table population is handled by CampaignWorkflow.js navigation handler
      // when user navigates to Step 2 (from Step 1 or back from Step 3).
      // This avoids duplicate INSERT calls that could cause duplicate user entries.

      // Fetch the attestation data
      const params = new URLSearchParams();
      if (campaign.campaign_id) {
        params.append('campaign_id', campaign.campaign_id);
      }
      // Add timestamp to prevent caching
      params.append('_t', Date.now());

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/prepare/?${params.toString()}`), {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();

      if (data.success) {
        setAttestationData(data);
      } else {
        setError('Failed to load attestation data');
      }
    } catch (err) {
      setError('Error loading attestation data: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter function
  const handleColumnFilter = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Get filtered and sorted data
  const getFilteredAndSortedUsers = () => {
    if (!attestationData) return [];

    let allUsers = [];

    // Add matched users with manager information
    if (attestationData.managers) {
      allUsers = attestationData.managers.flatMap(manager =>
        manager.assigned_users.map(user => ({
          ...user,
          manager_name: manager.manager_name,
          manager_email: manager.manager_email,
          manager_department: manager.manager_department
        }))
      );
    }

    // Add unmatched users with blank manager fields
    if (attestationData.unmatched_users) {
      const unmatchedUsersWithBlankManagers = attestationData.unmatched_users.map(user => ({
        ...user,
        manager_name: '',
        manager_email: '',
        manager_department: '',
        user_display_name: user.user_display_name || '',
        user_email: user.user_email || '',
        user_department: user.user_department || ''
      }));
      allUsers = [...allUsers, ...unmatchedUsersWithBlankManagers];
    }

    // Apply user filter (all/active)
    allUsers = allUsers.filter(user => {
      if (userFilter === 'all') return true;
      if (userFilter === 'active') return user.db_is_active === 'Y';
      return true;
    });

    // Mirror the backend's Stage 2 exclusion so the preview count matches what
    // populate will actually keep in scope. By default the backend excludes
    // is_system_account rows for every technology; only keep them when the
    // admin ticked "Include Database Internal Accounts".
    if (!includeSystemAccounts) {
      allUsers = allUsers.filter(user => !user.is_system_account);
    }

    // Apply privilege level filters (dynamically based on selected filters)
    const selectedPrivilegeLevels = Object.keys(accessFilters).filter(key => accessFilters[key]);

    if (selectedPrivilegeLevels.length > 0) {
      allUsers = allUsers.filter(user => {
        return selectedPrivilegeLevels.includes(user.privilege_level);
      });
    }

    // Apply column filters
    allUsers = allUsers.filter(user => {
      return Object.keys(columnFilters).every(key => {
        if (!columnFilters[key]) return true;

        let userValue = '';
        switch (key) {
          case 'db_username':
            userValue = user.db_username || '';
            break;
          case 'database_name':
            userValue = user.database_name || '';
            break;
          case 'physical_databases':
            userValue = user.physical_databases || '';
            break;
          case 'status':
            userValue = user.db_is_active === 'Y' ? 'Active' : 'Inactive';
            break;
          case 'user_display_name':
            userValue = user.user_display_name || '';
            break;
          case 'user_department':
            userValue = user.user_department || '';
            break;
          case 'manager_name':
            userValue = user.manager_name || '';
            break;
          case 'privilege_level':
            userValue = user.privilege_level || '';
            break;
          case 'risk_level':
            userValue = user.risk_level || '';
            break;
          default:
            return true;
        }

        return userValue.toLowerCase().includes(columnFilters[key].toLowerCase());
      });
    });

    // Apply sorting
    if (sortConfig.key) {
      allUsers.sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (sortConfig.key) {
          case 'db_username':
            aValue = a.db_username || '';
            bValue = b.db_username || '';
            break;
          case 'database_name':
            aValue = a.database_name || '';
            bValue = b.database_name || '';
            break;
          case 'physical_databases':
            aValue = a.physical_databases || '';
            bValue = b.physical_databases || '';
            break;
          case 'status':
            aValue = a.db_is_active === 'Y' ? 'Active' : 'Inactive';
            bValue = b.db_is_active === 'Y' ? 'Active' : 'Inactive';
            break;
          case 'user_display_name':
            aValue = a.user_display_name || '';
            bValue = b.user_display_name || '';
            break;
          case 'user_department':
            aValue = a.user_department || '';
            bValue = b.user_department || '';
            break;
          case 'manager_name':
            aValue = a.manager_name || '';
            bValue = b.manager_name || '';
            break;
          case 'privilege_level':
            // Sort by privilege level hierarchy using shared configuration
            aValue = PRIVILEGE_LEVEL_ORDER[a.privilege_level] || 0;
            bValue = PRIVILEGE_LEVEL_ORDER[b.privilege_level] || 0;
            break;
          case 'risk_level':
            const RISK_LEVEL_ORDER = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
            aValue = RISK_LEVEL_ORDER[a.risk_level] || 0;
            bValue = RISK_LEVEL_ORDER[b.risk_level] || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return allUsers;
  };

  // Get paginated users
  const getPaginatedUsers = () => {
    const filteredUsers = getFilteredAndSortedUsers();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  // Calculate summary statistics based on filtered data
  const getFilteredSummaryStats = () => {
    const filteredUsers = getFilteredAndSortedUsers();

    // Use backend's total_db_users from summary (already excludes role-only entries for Cassandra)
    // Fallback to counting users from managers/unmatched arrays if summary not available
    let totalDbUsers = attestationData?.summary?.total_db_users;
    if (totalDbUsers === undefined) {
      let allUsers = [];
      if (attestationData && attestationData.managers) {
        allUsers = attestationData.managers.flatMap(manager =>
          manager.assigned_users.map(user => ({
            ...user,
            manager_name: manager.manager_name,
            manager_email: manager.manager_email,
            manager_department: manager.manager_department
          }))
        );
      }
      if (attestationData && attestationData.unmatched_users) {
        allUsers = [...allUsers, ...attestationData.unmatched_users];
      }
      totalDbUsers = allUsers.length;
    }

    const matched = filteredUsers.filter(user =>
      user.match_status && user.match_status.toLowerCase() === 'matched' && user.manager_id
    ).length;
    const usersWithoutManagers = filteredUsers.filter(user =>
      !user.manager_id
    ).length;

    // Count unique managers from filtered users
    const uniqueManagers = new Set();
    filteredUsers.forEach(user => {
      if (user.manager_id) {
        uniqueManagers.add(user.manager_id);
      }
    });

    return {
      total_db_users: totalDbUsers,  // From backend summary (excludes roles for Cassandra)
      total_roles: attestationData?.summary?.total_roles || 0,  // Roles count (for Cassandra)
      selected_users: filteredUsers.length,  // After applying filters
      matched_users: matched,
      users_without_managers: usersWithoutManagers,  // Renamed from unmatched_users
      total_managers: uniqueManagers.size,
      override_users: 0,
      app_owner_users: 0
    };
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [userFilter, accessFilters, columnFilters, sortConfig]);

  // Calculate pagination info
  const totalUsers = getFilteredAndSortedUsers().length;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const startItem = totalUsers === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalUsers);

  if (isLoading) {
    return (
      <div className="mt-4">
        <div className="p-3 bg-info-subtle border border-info rounded">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading attestation preparation data...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-2" onClick={loadAttestationData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!attestationData) {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="p-3 bg-warning-subtle border border-warning rounded mb-3">
        {/* Filter Options */}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
            <span className="fw-semibold text-nowrap" style={{ fontSize: '0.85rem' }}>
              <i className="fas fa-filter text-primary me-1"></i>Status:
            </span>
            <div className="form-check form-check-inline mb-0">
              <input
                className="form-check-input"
                type="radio"
                name="userFilter"
                id="filterAll"
                value="all"
                checked={userFilter === 'all'}
                onChange={(e) => setUserFilter(e.target.value)}
              />
              <label className="form-check-label" htmlFor="filterAll">All</label>
            </div>
            <div className="form-check form-check-inline mb-0">
              <input
                className="form-check-input"
                type="radio"
                name="userFilter"
                id="filterActive"
                value="active"
                checked={userFilter === 'active'}
                onChange={(e) => setUserFilter(e.target.value)}
              />
              <label className="form-check-label" htmlFor="filterActive">Active Only</label>
            </div>
            <button
              className="btn btn-warning btn-sm ms-auto"
              onClick={loadAttestationData}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-1"></i>
              Refresh
            </button>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="fw-semibold text-nowrap" style={{ fontSize: '0.85rem' }}>
              <i className="fas fa-shield-alt text-primary me-1"></i>Privilege:
            </span>
            {getUniquePrivilegeLevels().map(level => {
              const levelTextClass = {
                'SUPER_ADMIN': 'text-danger fw-semibold',
                'ADMIN': 'text-danger',
                'WRITE_ELEVATED': 'text-warning',
                'WRITE': 'text-primary',
                'READ_ELEVATED': 'text-info',
                'READ': 'text-success',
                'NONE': 'text-muted'
              };
              const textClass = levelTextClass[level] || 'text-muted';

              return (
                <div className="form-check form-check-inline mb-0" key={level}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`filter${level}`}
                    checked={accessFilters[level] || false}
                    onChange={(e) => setAccessFilters(prev => ({
                      ...prev,
                      [level]: e.target.checked
                    }))}
                  />
                  <label className="form-check-label" htmlFor={`filter${level}`}>
                    <span className={textClass}>{level.replace(/_/g, ' ')}</span>
                  </label>
                </div>
              );
            })}
          </div>
          {/* "Include Database Internal Accounts" applies to every tech: Oracle
              maintained, MSSQL system principals (sa/dbo/NT AUTHORITY/##MS_*##),
              MySQL root/mysql.*, postgres, cassandra. Excluded by default; routed
              to the per-tech default manager when opted in (see DatabaseOwners). */}
          <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeSystemAccounts"
                checked={includeSystemAccounts}
                onChange={(e) => setIncludeSystemAccounts(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="includeSystemAccounts" style={{ fontSize: '0.85rem' }}>
                <i className="fas fa-cog text-secondary me-1"></i>
                Include Database Internal Accounts
              </label>
            </div>
            <small className="text-muted">
              (excluded by default; when included, routed to the per-technology default manager)
            </small>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="alert alert-info mb-3">
          <div className="row text-center justify-content-around">
            <div className="col-md-2">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <div className="d-flex align-items-center mb-1 text-nowrap">
                  <i className="fas fa-database text-primary me-2"></i>
                  <strong>Total DB Users</strong>
                </div>
                <div className="badge bg-primary">{getFilteredSummaryStats().total_db_users}</div>
              </div>
            </div>
            {/* Show Roles count for Cassandra campaigns */}
            {campaign.database_type === 'CASSANDRA' && getFilteredSummaryStats().total_roles > 0 && (
              <div className="col-md-2">
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div className="d-flex align-items-center mb-1 text-nowrap">
                    <i className="fas fa-user-tag text-secondary me-2"></i>
                    <strong>Roles (Excluded)</strong>
                  </div>
                  <div className="badge bg-secondary">{getFilteredSummaryStats().total_roles}</div>
                </div>
              </div>
            )}
            <div className="col-md-2">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <div className="d-flex align-items-center mb-1 text-nowrap">
                  <i className="fas fa-filter text-secondary me-2"></i>
                  <strong>Selected Users</strong>
                </div>
                <div className="badge bg-secondary">{getFilteredSummaryStats().selected_users}</div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <div className="d-flex align-items-center mb-1 text-nowrap">
                  <i className="fas fa-user-check text-success me-2"></i>
                  <strong>Matched</strong>
                </div>
                <div className="badge bg-success">{getFilteredSummaryStats().matched_users}</div>
              </div>
            </div>
            {getFilteredSummaryStats().override_users > 0 && (
              <div className="col-md-2">
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div className="d-flex align-items-center mb-1 text-nowrap">
                    <i className="fas fa-user-shield text-warning me-2"></i>
                    <strong>Account Owner</strong>
                  </div>
                  <div className="badge bg-warning">{getFilteredSummaryStats().override_users}</div>
                </div>
              </div>
            )}
            <div className="col-md-2">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <div className="d-flex align-items-center mb-1 text-nowrap">
                  <i className="fas fa-users text-info me-2"></i>
                  <strong>Managers</strong>
                </div>
                <div className="badge bg-info">{getFilteredSummaryStats().total_managers}</div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Info banner */}
      {attestationData && (
        <div className="alert alert-light border mb-3 py-2 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
          <i className="fas fa-info-circle text-primary me-2"></i>
          <span>
            <strong>No action required on individual users.</strong> The list below shows all database users that match your selected filters. These users are already prepared for attestation and will be automatically included when you proceed to the next step.
          </span>
        </div>
      )}

      {/* Database Users Table - Separate Card */}
      {attestationData && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header" style={{ backgroundColor: 'var(--background-secondary)', borderBottom: '2px solid var(--border-light)' }}>
            <h6 className="fw-semibold mb-0">
              <i className="fas fa-users text-primary me-2"></i>
              Database Users
            </h6>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead style={{ backgroundColor: 'var(--background-secondary)', borderBottom: '2px solid var(--border-light)', fontSize: '0.85rem' }}>
                  <tr>
                    <th
                      style={{ cursor: 'pointer', maxWidth: '100px', whiteSpace: 'normal', wordWrap: 'break-word', color: 'var(--text-primary)', fontWeight: '600', padding: 'var(--space-sm)' }}
                      onClick={() => handleSort('db_username')}
                    >
                      Database User
                      <i className={`fas fa-sort ms-1 ${sortConfig.key === 'db_username' ?
                        (sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : ''}`}></i>
                    </th>
                    <th
                      style={{ cursor: 'pointer', maxWidth: '80px', whiteSpace: 'normal', wordWrap: 'break-word', color: 'var(--text-primary)', fontWeight: '600', padding: 'var(--space-sm)' }}
                      onClick={() => handleSort('database_name')}
                    >
                      {isMssql ? 'Instance Name' : 'Database'}
                      <i className={`fas fa-sort ms-1 ${sortConfig.key === 'database_name' ?
                        (sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : ''}`}></i>
                    </th>
                    {isMssql && (
                      <th
                        style={{ cursor: 'pointer', maxWidth: '140px', whiteSpace: 'normal', wordWrap: 'break-word', color: 'var(--text-primary)', fontWeight: '600', padding: 'var(--space-sm)' }}
                        onClick={() => handleSort('physical_databases')}
                      >
                        DB Names
                        <i className={`fas fa-sort ms-1 ${sortConfig.key === 'physical_databases' ?
                          (sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : ''}`}></i>
                      </th>
                    )}
                    <th
                      style={{ cursor: 'pointer', whiteSpace: 'normal', wordWrap: 'break-word', color: 'var(--text-primary)', fontWeight: '600', padding: 'var(--space-sm)' }}
                      onClick={() => handleSort('status')}
                    >
                      Status
                      <i className={`fas fa-sort ms-1 ${sortConfig.key === 'status' ?
                        (sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : ''}`}></i>
                    </th>
                    <th
                      style={{ cursor: 'pointer', color: 'var(--text-primary)', fontWeight: '600', padding: 'var(--space-sm)' }}
                      onClick={() => handleSort('privilege_level')}
                    >
                      Privilege Level
                      <i className={`fas fa-sort ms-1 ${sortConfig.key === 'privilege_level' ?
                        (sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : ''}`}></i>
                    </th>
                  </tr>
                  <tr>
                    <th style={{ maxWidth: '100px', backgroundColor: 'var(--background-secondary)', padding: 'var(--space-sm)' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="🔍 User"
                        value={columnFilters.db_username}
                        onChange={(e) => handleColumnFilter('db_username', e.target.value)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.4rem', width: '100%' }}
                      />
                    </th>
                    <th style={{ maxWidth: '80px', backgroundColor: 'var(--background-secondary)', padding: 'var(--space-sm)' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={isMssql ? '🔍 Instance' : '🔍 DB'}
                        value={columnFilters.database_name}
                        onChange={(e) => handleColumnFilter('database_name', e.target.value)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.4rem', width: '100%' }}
                      />
                    </th>
                    {isMssql && (
                      <th style={{ maxWidth: '140px', backgroundColor: 'var(--background-secondary)', padding: 'var(--space-sm)' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="🔍 DB Names"
                          value={columnFilters.physical_databases}
                          onChange={(e) => handleColumnFilter('physical_databases', e.target.value)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.4rem', width: '100%' }}
                        />
                      </th>
                    )}
                    <th style={{ backgroundColor: 'var(--background-secondary)', padding: 'var(--space-sm)' }}>
                      <select
                        className="form-select form-select-sm"
                        value={columnFilters.status}
                        onChange={(e) => handleColumnFilter('status', e.target.value)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.4rem' }}
                      >
                        <option value="">All</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </th>
                    <th style={{ backgroundColor: 'var(--background-secondary)', padding: 'var(--space-sm)' }}>
                      <select
                        className="form-select form-select-sm"
                        value={columnFilters.privilege_level}
                        onChange={(e) => handleColumnFilter('privilege_level', e.target.value)}
                      >
                        <option value="">All</option>
                        {getUniquePrivilegeLevels()
                          .sort((a, b) => (PRIVILEGE_LEVEL_ORDER[b] || 0) - (PRIVILEGE_LEVEL_ORDER[a] || 0))
                          .map(level => (
                            <option key={level} value={level}>{level.replace(/_/g, ' ')}</option>
                          ))
                        }
                      </select>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedUsers().map((user, index) => (
                    <tr
                      key={index}>

                      <td style={{ maxWidth: '100px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        <div className="fw-semibold">{user.db_username}</div>
                        <small className="text-muted">
                          {user.database_type}
                          {/* MSSQL: Show principal type (SQL_LOGIN, WINDOWS_LOGIN, SQL_USER, etc.) or fallback to scope */}
                          {(user.principal_type || user.principal_scope) && (
                            <span className="ms-1" title={user.principal_type || user.principal_scope}>
                              <i className={`fas ${
                                user.principal_type ? (
                                  user.principal_type.includes('LOGIN') ? 'fa-server' :
                                  user.principal_type.includes('GROUP') ? 'fa-users' : 'fa-database'
                                ) : (
                                  user.principal_scope === 'SERVER' ? 'fa-server' : 'fa-database'
                                )
                              } ms-1`}></i>
                              <span className="ms-1">
                                {user.principal_type
                                  ? user.principal_type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                                  : (user.principal_scope === 'SERVER' ? 'Server Login' : 'Database User')
                                }
                              </span>
                            </span>
                          )}
                        </small>
                      </td>
                      <td style={{ maxWidth: '80px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{user.database_name}</td>
                      {isMssql && (
                        <td style={{ maxWidth: '140px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                          {user.physical_databases
                            ? user.physical_databases.split(',').map((db, i) => (
                                <span key={i} className="badge bg-light text-dark border me-1 mb-1" style={{ fontWeight: 400 }}>
                                  {db.trim()}
                                </span>
                              ))
                            : <span className="text-muted">—</span>}
                        </td>
                      )}
                      <td>
                        <span className={user.db_is_active === 'Y' ? 'text-success' : 'text-danger'}>
                          {user.db_is_active === 'Y' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <span className={
                          user.privilege_level === 'SUPER_ADMIN' ? 'text-danger fw-semibold' :
                          user.privilege_level === 'ADMIN' ? 'text-danger' :
                          user.privilege_level === 'WRITE_ELEVATED' ? 'text-warning' :
                          user.privilege_level === 'WRITE' ? 'text-primary' :
                          user.privilege_level === 'READ_ELEVATED' ? 'text-info' :
                          user.privilege_level === 'READ' ? 'text-success' : 'text-muted'
                        }>
                          <i className={`fas ${
                            ['WRITE', 'WRITE_ELEVATED', 'ADMIN', 'SUPER_ADMIN'].includes(user.privilege_level) ? 'fa-edit' : 'fa-eye'
                          } me-1`}></i>
                          {user.privilege_level}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <span className="text-muted me-3">
                    Showing {startItem}-{endItem} of {totalUsers} users
                  </span>
                  <div className="d-flex align-items-center">
                    <label className="form-label me-2 mb-0">Items per page:</label>
                    <select
                      className="form-select form-select-sm"
                      style={{width: 'auto'}}
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <nav aria-label="User list pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {/* Page numbers */}
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }
                      return pages;
                    })()}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};


export default AttestationPreparation;
